// src/iap/appleIap.js
//
// Thin wrapper around @revenuecat/purchases-capacitor. Everything here is a
// no-op unless isAppleIap() is true (native iOS + RevenueCat key), so importing
// it from the shared web panels is safe — the RevenueCat plugin is loaded
// LAZILY (dynamic import) and only on iOS, so the web/Android webpack bundle
// never pulls in native code.
//
// Purchase truth is established server-side by the RevenueCat webhook
// (see backend/routes/commerce/server-iap-apple.js). The client's job is only
// to (a) present Apple's native purchase sheet and (b) tell the caller when the
// transaction finished so the wallet can re-fetch. We never credit from the
// client.

import {
  isAppleIap,
  REVENUECAT_IOS_KEY,
  coinProductId,
  membershipProductId,
} from './appleIapConfig';

let _purchasesPromise = null;
let _configuredForUid = '';

// Dynamically load the plugin only on iOS. Returns the Purchases module or null.
async function loadPurchases() {
  if (!isAppleIap()) return null;
  if (!_purchasesPromise) {
    _purchasesPromise = import('@revenuecat/purchases-capacitor')
      .then((m) => m.Purchases)
      .catch((e) => {
        console.warn('[iap] RevenueCat plugin unavailable:', e?.message || e);
        _purchasesPromise = null;
        return null;
      });
  }
  return _purchasesPromise;
}

/**
 * Configure RevenueCat and identify the user with their Firebase uid. MUST be
 * called (idempotently) before any purchase so RevenueCat's appUserID matches
 * the uid the backend webhook will credit. Safe to call repeatedly.
 */
export async function ensureIapReady(uid = '') {
  const Purchases = await loadPurchases();
  if (!Purchases) return false;
  const safeUid = String(uid || '').trim();

  try {
    if (_configuredForUid !== safeUid) {
      await Purchases.configure({
        apiKey: REVENUECAT_IOS_KEY,
        // Pin RevenueCat's identity to the Firebase uid so webhook events carry
        // it as app_user_id — that's how the backend knows whose wallet to fill.
        appUserID: safeUid || undefined,
      });
      _configuredForUid = safeUid;
    }
    return true;
  } catch (e) {
    console.warn('[iap] configure failed:', e?.message || e);
    return false;
  }
}

// Find a StoreProduct by its Apple product identifier.
async function getProduct(Purchases, productId) {
  if (!productId) throw new Error('IAP product not configured');
  const res = await Purchases.getProducts({ productIdentifiers: [productId] });
  const product = (res?.products || []).find(
    (p) => p.identifier === productId
  );
  if (!product) throw new Error(`IAP product not found: ${productId}`);
  return product;
}

/**
 * Buy a coin bundle via Apple IAP.
 * @returns {Promise<{ok:boolean, transactionId?:string, cancelled?:boolean, error?:string}>}
 */
export async function purchaseCoins({ uid, packageId }) {
  const Purchases = await loadPurchases();
  if (!Purchases) return { ok: false, error: 'IAP unavailable' };
  const productId = coinProductId(packageId);
  if (!productId) return { ok: false, error: `No IAP product for ${packageId}` };

  await ensureIapReady(uid);
  try {
    const product = await getProduct(Purchases, productId);
    const result = await Purchases.purchaseStoreProduct({ product });
    return {
      ok: true,
      transactionId: extractTxnId(result),
    };
  } catch (e) {
    return normalizePurchaseError(e);
  }
}

/**
 * Subscribe to a membership tier via Apple IAP.
 * @returns {Promise<{ok:boolean, transactionId?:string, cancelled?:boolean, error?:string}>}
 */
export async function purchaseMembership({ uid, planKey, cycle = 'monthly' }) {
  const Purchases = await loadPurchases();
  if (!Purchases) return { ok: false, error: 'IAP unavailable' };
  const productId = membershipProductId(planKey, cycle);
  if (!productId) return { ok: false, error: `No IAP product for ${planKey}/${cycle}` };

  await ensureIapReady(uid);
  try {
    const product = await getProduct(Purchases, productId);
    const result = await Purchases.purchaseStoreProduct({ product });
    return { ok: true, transactionId: extractTxnId(result) };
  } catch (e) {
    return normalizePurchaseError(e);
  }
}

/**
 * Restore purchases (Apple requires a visible "Restore Purchases" control for
 * apps that sell non-consumable / subscription content). Re-syncs entitlements
 * so the webhook / customerInfo reflect an existing subscription on a new
 * device or reinstall.
 */
export async function restorePurchases(uid = '') {
  const Purchases = await loadPurchases();
  if (!Purchases) return { ok: false, error: 'IAP unavailable' };
  await ensureIapReady(uid);
  try {
    const info = await Purchases.restorePurchases();
    return { ok: true, customerInfo: info?.customerInfo || info || null };
  } catch (e) {
    return normalizePurchaseError(e);
  }
}

function extractTxnId(result) {
  // Plugin shape varies across versions; try the common locations.
  return (
    result?.transaction?.transactionIdentifier ||
    result?.transaction?.transactionId ||
    result?.customerInfo?.originalAppUserId ||
    ''
  );
}

function normalizePurchaseError(e) {
  const cancelled =
    e?.code === 'PURCHASE_CANCELLED' ||
    e?.userCancelled === true ||
    /cancel/i.test(String(e?.message || ''));
  if (cancelled) return { ok: false, cancelled: true };
  return { ok: false, error: e?.message || 'Purchase failed' };
}

export { isAppleIap };
